import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormArray,
} from '@angular/forms';
import { Question, QuestionOption } from 'core/models/course-content.model';
import { ConfirmDialogService } from 'core/services/confirm-dialog.service';

@Component({
  selector: 'app-question-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './question-form.html',
})
export class QuestionForm implements OnInit {
  @Input() question: Question | null = null;
  @Input() isEditMode = false;
  @Output() save = new EventEmitter<Question>();
  @Output() canceled = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  protected questionForm!: FormGroup;
  protected questionTypes = [
    { value: 'single-choice', label: 'Seleção Única' },
    { value: 'multiple-choice', label: 'Seleção Múltipla' },
    { value: 'boolean', label: 'Verdadeiro ou Falso' },
  ];
  // Mapa para preservar IDs originais das opções (label -> id)
  private originalOptionIds = new Map<string, string>();

  ngOnInit(): void {
    this.questionForm = this.fb.group({
      text: [
        this.question?.text || '',
        [Validators.required, Validators.minLength(10)],
      ],
      type: [this.question?.type || 'single-choice', Validators.required],
      points: [
        this.question?.points || 1,
        [Validators.required, Validators.min(1)],
      ],
      options: this.fb.array([]),
      correctAnswers: [[], Validators.required],
    });

    if (this.question?.options) {
      // Limpar mapa de IDs originais
      this.originalOptionIds.clear();
      this.question.options.forEach((option) => {
        // Armazenar mapeamento label -> id original
        this.originalOptionIds.set(option.label, option.id);
        this.addOption(option.label, option.text);
      });
    } else {
      this.originalOptionIds.clear();
      this.initializeDefaultOptions();
    }

    if (this.question?.correctAnswers && this.question?.options) {
      // Converter IDs das respostas corretas (opt1, opt2, etc.) para labels (a, b, c, etc.)
      const correctLabels = this.question.correctAnswers
        .map((answerId) => {
          const option = this.question?.options.find(
            (opt) => opt.id === answerId,
          );
          return option?.label;
        })
        .filter((label): label is string => !!label);

      this.questionForm.patchValue({
        correctAnswers: correctLabels,
      });
    }

    this.questionForm.get('type')?.valueChanges.subscribe((type) => {
      this.onTypeChange(type);
    });
  }

  protected get optionsFormArray(): FormArray {
    return this.questionForm.get('options') as FormArray;
  }

  protected addOption(label = '', text = ''): void {
    const optionGroup = this.fb.group({
      label: [label, Validators.required],
      text: [text, Validators.required],
    });
    this.optionsFormArray.push(optionGroup);
  }

  protected async removeOption(index: number): Promise<void> {
    const option = this.optionsFormArray.at(index);
    const label = option.get('label')?.value;
    const optionText = option.get('text')?.value || '';

    const confirmed = await this.confirmDialogService.confirm({
      title: 'Excluir Opção',
      message: `Tem certeza que deseja excluir a opção "${this.formatOptionLabel(label)} ${optionText}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (!confirmed) {
      return;
    }

    const correctAnswers = this.questionForm.get('correctAnswers')?.value || [];
    const wasCorrectAnswer = correctAnswers.includes(label);

    // Remover a opção
    this.optionsFormArray.removeAt(index);

    // Remover do mapa de IDs originais
    this.originalOptionIds.delete(label);

    // Renumerar as opções restantes para manter sequência alfabética (a, b, c, d...)
    const oldLabels: string[] = [];
    this.optionsFormArray.controls.forEach((control) => {
      oldLabels.push(control.get('label')?.value);
    });

    this.optionsFormArray.controls.forEach((control, i) => {
      const oldLabel = oldLabels[i];
      const newLabel = String.fromCharCode(97 + i); // 97 = 'a' em ASCII

      // Atualizar o mapa de IDs se o label mudou
      if (oldLabel && oldLabel !== newLabel) {
        const originalId = this.originalOptionIds.get(oldLabel);
        if (originalId) {
          this.originalOptionIds.delete(oldLabel);
          this.originalOptionIds.set(newLabel, originalId);
        }
      }

      control.patchValue({ label: newLabel }, { emitEvent: false });
    });

    // Atualizar correctAnswers com os novos labels
    let updatedCorrectAnswers: string[] = [];
    if (wasCorrectAnswer) {
      // Se a opção deletada era correta, não há mais resposta correta
      updatedCorrectAnswers = [];
    } else {
      // Mapear os labels antigos para os novos
      const oldLabels = correctAnswers.filter(
        (answer: string) => answer !== label,
      );
      updatedCorrectAnswers = oldLabels.map((oldLabel: string) => {
        const oldIndex = oldLabel.charCodeAt(0) - 97; // Converter 'a'=0, 'b'=1, etc.
        if (oldIndex > index) {
          // Se a opção estava depois da deletada, seu índice diminui em 1
          return String.fromCharCode(97 + oldIndex - 1);
        }
        // Se estava antes, mantém o mesmo índice
        return String.fromCharCode(97 + oldIndex);
      });
    }

    this.questionForm.patchValue({ correctAnswers: updatedCorrectAnswers });
    this.questionForm.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  protected initializeDefaultOptions(): void {
    const type = this.questionForm.get('type')?.value;
    if (type === 'boolean') {
      this.addOption('a', 'Verdadeiro');
      this.addOption('b', 'Falso');
    } else {
      this.addOption('a', '');
      this.addOption('b', '');
      this.addOption('c', '');
      this.addOption('d', '');
    }
  }

  protected onTypeChange(type: string): void {
    const currentOptions = this.optionsFormArray.length;

    if (type === 'boolean') {
      this.optionsFormArray.clear();
      this.originalOptionIds.clear(); // Limpar mapa quando mudar para boolean
      this.addOption('a', 'Verdadeiro');
      this.addOption('b', 'Falso');
      this.questionForm.patchValue({ correctAnswers: [] });
    } else {
      if (currentOptions < 2) {
        // Limpar mapa quando adicionar novas opções
        this.originalOptionIds.clear();
        while (this.optionsFormArray.length < 4) {
          this.addOption(
            String.fromCharCode(97 + this.optionsFormArray.length),
            '',
          );
        }
      }
    }
  }

  protected toggleCorrectAnswer(optionIndex: number): void {
    const option = this.optionsFormArray.at(optionIndex);
    const label = option.get('label')?.value;
    const type = this.questionForm.get('type')?.value;
    const currentCorrectAnswers =
      this.questionForm.get('correctAnswers')?.value || [];

    let updatedAnswers: string[];

    if (type === 'single-choice' || type === 'boolean') {
      updatedAnswers = [label];
    } else {
      const index = currentCorrectAnswers.indexOf(label);
      if (index > -1) {
        updatedAnswers = currentCorrectAnswers.filter(
          (a: string) => a !== label,
        );
      } else {
        updatedAnswers = [...currentCorrectAnswers, label];
      }
    }

    this.questionForm.patchValue({ correctAnswers: updatedAnswers });
  }

  protected isCorrectAnswer(optionIndex: number): boolean {
    const option = this.optionsFormArray.at(optionIndex);
    const label = option.get('label')?.value;
    const correctAnswers = this.questionForm.get('correctAnswers')?.value || [];
    return correctAnswers.includes(label);
  }

  protected onSave(): void {
    if (this.questionForm.invalid) {
      this.questionForm.markAllAsTouched();
      return;
    }

    const formValue = this.questionForm.value;
    // Preservar IDs originais das opções se existirem, caso contrário criar novos
    const options: QuestionOption[] = formValue.options.map(
      (opt: { label: string; text: string }, index: number) => {
        // Tentar encontrar o ID original pelo label no mapa
        const originalId = this.originalOptionIds.get(opt.label);
        return {
          id: originalId || `opt${index + 1}`,
          label: opt.label,
          text: opt.text,
        };
      },
    );

    // Converter labels das respostas corretas para IDs das opções
    const correctAnswerIds = formValue.correctAnswers
      .map((label: string) => {
        const option = options.find((opt) => opt.label === label);
        return option?.id;
      })
      .filter((id: string | undefined): id is string => !!id);

    const question: Question = {
      id: this.question?.id || `q${Date.now()}`,
      text: formValue.text,
      type: formValue.type,
      points: formValue.points,
      options,
      correctAnswers: correctAnswerIds,
      order: this.question?.order || 0,
    };

    this.save.emit(question);
  }

  protected onCancel(): void {
    this.canceled.emit();
  }

  protected getFieldError(fieldName: string): string {
    const field = this.questionForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return '';
    }

    if (field.errors['required']) {
      return 'Este campo é obrigatório';
    }
    if (field.errors['minlength']) {
      return 'Deve ter pelo menos 10 caracteres';
    }
    if (field.errors['min']) {
      return 'Valor deve ser maior que 0';
    }

    return '';
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.questionForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Gera o próximo label de opção (a, b, c, d, etc.)
   */
  protected getNextOptionLabel(): string {
    return String.fromCharCode(97 + this.optionsFormArray.length);
  }

  /**
   * Formata o label da opção com parênteses para exibição (a), b), c), etc.)
   * @param label Label da opção (a, b, c, d, etc.)
   * @returns Label formatado com parênteses
   */
  protected formatOptionLabel(label: string): string {
    return `${label})`;
  }
}
